import { useEffect, useState } from 'react';

import { baseTokens } from './tokens/index.js';

/** MD3 window size classes used in Phase 1 layouts (components.md §2.6). */
export type WindowClass = 'compact' | 'medium' | 'expanded';

const COMPACT_MAX = Number.parseInt(baseTokens.window.compactMax, 10);
const MEDIUM_MAX = Number.parseInt(baseTokens.window.mediumMax, 10);

/** Maps a viewport width in CSS pixels to a window size class. */
export function windowClassFromWidth(width: number): WindowClass {
  if (width <= COMPACT_MAX) {
    return 'compact';
  }
  if (width <= MEDIUM_MAX) {
    return 'medium';
  }
  return 'expanded';
}

/**
 * Subscribes to `window.innerWidth` and returns the active MD3 window class. In tests and SSR it
 * returns `fallback` until a width is known.
 */
export function useWindowClass(fallback: WindowClass = 'compact'): WindowClass {
  const [windowClass, setWindowClass] = useState<WindowClass>(() => {
    if (typeof window === 'undefined') {
      return fallback;
    }
    return windowClassFromWidth(window.innerWidth);
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const update = () => setWindowClass(windowClassFromWidth(window.innerWidth));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return windowClass;
}
